import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Upload, Globe, Database, Loader2, AlertCircle, File, ArrowRight, FileText } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { supabase } from '../lib/supabase';
import { FileProcessor } from '../lib/fileProcessors';
import { useTranslation } from '../lib/i18n';

const LoadDocument = () => {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [urlInput, setUrlInput] = useState('');
  const [activeTab, setActiveTab] = useState<'computer' | 'supabase' | 'url'>('computer');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [processingProgress, setProcessingProgress] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const validateAndSetFile = (file: File) => {
    const validation = FileProcessor.validateFileType(file);

    if (!validation.valid) {
      setError(t('loadDocument.invalidFile'));
      return;
    }

    setSelectedFile(file);
    setError(null);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      setError(t('loadDocument.selectFileFirst'));
      return;
    }

    setLoading(true);
    setError(null);
    setProcessingProgress(t('loadDocument.processingFile'));

    try {
      const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();

      if (['.pdf', '.docx'].includes(fileExtension)) {
        setProcessingProgress(t('loadDocument.extractingText', { format: fileExtension.substring(1).toUpperCase() }));
      }

      const result = await FileProcessor.processFile(selectedFile);

      setProcessingProgress(t('loadDocument.savingToDatabase'));

      const { data, error: uploadError } = await supabase
        .from('documents')
        .insert([
          {
            title: selectedFile.name.replace(/\.[^/.]+$/, ''),
            content: { text: result.text },
            user_id: user?.id,
            file_type: result.metadata.fileType,
            file_metadata: result.metadata,
          },
        ])
        .select()
        .single();

      if (uploadError) throw uploadError;

      if (data?.id) {
        setProcessingProgress('');
        navigate(`/editor/${data.id}`);
      }
    } catch (err: any) {
      console.error('Error uploading file:', err);
      setError(err.message || t('loadDocument.failedUpload'));
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } finally {
      setLoading(false);
      setProcessingProgress('');
    }
  };

  const handleUrlLoad = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!urlInput.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(urlInput);
      if (!response.ok) {
        throw new Error(t('loadDocument.failedFetchUrl'));
      }

      const text = await response.text();
      const sanitizedText = text
        .replace(/\0/g, '')
        .replace(/[\uFFFD\uFFFE\uFFFF]/g, '');
      
      if (!sanitizedText.trim()) {
        throw new Error(t('loadDocument.emptyUrl'));
      }

      const urlParts = urlInput.split('/');
      const fileName = urlParts[urlParts.length - 1].replace(/\.[^/.]+$/, '') || t('loadDocument.importedDoc');

      const { data, error: uploadError } = await supabase
        .from('documents')
        .insert([
          {
            title: fileName,
            content: { text: sanitizedText },
            user_id: user?.id,
          },
        ])
        .select()
        .single();

      if (uploadError) throw uploadError;

      if (data?.id) {
        navigate(`/editor/${data.id}`);
      }
    } catch (err: any) {
      console.error('Error loading from URL:', err);
      setError(err.message || t('loadDocument.failedLoadUrl'));
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="text-center space-y-6 p-8 max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <Upload className="w-16 h-16 text-blue-500 mx-auto" />
          <h2 className="text-2xl font-bold">{t('loadDocument.pleaseSignIn')}</h2>
          <p className="text-gray-600 dark:text-gray-300">
            {t('loadDocument.pleaseSignInMessage')}
          </p>
          <Button onClick={() => navigate('/auth')} className="w-full">
            {t('loadDocument.signInToContinue')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
          {t('loadDocument.title')}
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-300">
          {t('loadDocument.subtitle')}
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-100 dark:bg-red-900/50 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 px-4 py-3 rounded-lg animate-fadeIn">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* From Computer Card */}
        <div 
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden transition-all duration-200 ${
            activeTab === 'computer' ? 'ring-2 ring-blue-500 transform scale-105' : 'hover:shadow-xl'
          }`}
          onClick={() => setActiveTab('computer')}
        >
          <div className="p-6 space-y-4">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
              <Upload className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-xl font-semibold">{t('loadDocument.fromComputer')}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {t('loadDocument.fromComputerDesc')}
            </p>
          </div>
        </div>

        {/* From Supabase Card */}
        <div 
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden transition-all duration-200 ${
            activeTab === 'supabase' ? 'ring-2 ring-blue-500 transform scale-105' : 'hover:shadow-xl'
          }`}
          onClick={() => setActiveTab('supabase')}
        >
          <div className="p-6 space-y-4">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/50 rounded-lg flex items-center justify-center">
              <Database className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <h3 className="text-xl font-semibold">{t('loadDocument.fromSupabase')}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {t('loadDocument.fromSupabaseDesc')}
            </p>
          </div>
        </div>

        {/* From URL Card */}
        <div 
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden transition-all duration-200 ${
            activeTab === 'url' ? 'ring-2 ring-blue-500 transform scale-105' : 'hover:shadow-xl'
          }`}
          onClick={() => setActiveTab('url')}
        >
          <div className="p-6 space-y-4">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/50 rounded-lg flex items-center justify-center">
              <Globe className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-xl font-semibold">{t('loadDocument.fromUrl')}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {t('loadDocument.fromUrlDesc')}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
        {activeTab === 'computer' && (
          <div className="space-y-6">
            <div 
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragging 
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                  : 'border-gray-300 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-500'
              }`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={FileProcessor.getAcceptAttribute()}
                onChange={handleFileSelect}
                className="hidden"
                disabled={loading}
              />
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">
                {t('loadDocument.dragDrop')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {t('loadDocument.or')}
              </p>
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
                variant="outline"
              >
                <Upload className="w-4 h-4 mr-2" />
                {t('loadDocument.browseFiles')}
              </Button>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
                {t('loadDocument.supportedFormats')}: {FileProcessor.getFormatDescription()}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('loadDocument.maxFileSize')}
              </p>
            </div>

            {selectedFile && (
              <div className="space-y-3">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {selectedFile.name.toLowerCase().endsWith('.pdf') ? (
                      <FileText className="w-8 h-8 text-red-500" />
                    ) : selectedFile.name.toLowerCase().endsWith('.docx') ? (
                      <FileText className="w-8 h-8 text-blue-500" />
                    ) : (
                      <File className="w-8 h-8 text-gray-500" />
                    )}
                    <div>
                      <p className="font-medium">{selectedFile.name}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {selectedFile.size > 1024 * 1024
                          ? `${(selectedFile.size / (1024 * 1024)).toFixed(2)} MB`
                          : `${(selectedFile.size / 1024).toFixed(2)} KB`}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={handleFileUpload}
                    disabled={loading}
                    className="ml-4"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('loadDocument.processing')}
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        {t('loadDocument.upload')}
                      </>
                    )}
                  </Button>
                </div>
                {processingProgress && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600 dark:text-blue-400" />
                    <p className="text-sm text-blue-700 dark:text-blue-300">{processingProgress}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'supabase' && (
          <div className="text-center space-y-6">
            <Database className="w-16 h-16 text-purple-500 mx-auto" />
            <div>
              <h3 className="text-xl font-semibold mb-2">
                {t('loadDocument.yourSavedDocuments')}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                {t('loadDocument.savedDocumentsDesc')}
              </p>
            </div>
            <Button
              onClick={() => navigate('/documents')}
              disabled={loading}
              size="lg"
              className="px-8"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {t('loadDocument.loading')}
                </>
              ) : (
                <>
                  <Database className="w-5 h-5 mr-2" />
                  {t('loadDocument.viewDocuments')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </div>
        )}

        {activeTab === 'url' && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <Globe className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">
                {t('loadDocument.importFromUrl')}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                {t('loadDocument.importFromUrlDesc')}
              </p>
            </div>
            
            <form onSubmit={handleUrlLoad} className="max-w-2xl mx-auto">
              <div className="flex gap-3">
                <input
                  type="url"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://example.com/document.txt"
                  className="flex-1 px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 dark:bg-gray-700"
                  required
                  disabled={loading}
                />
                <Button 
                  type="submit" 
                  disabled={loading}
                  size="lg"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {t('loadDocument.loading')}
                    </>
                  ) : (
                    <>
                      <Globe className="w-5 h-5 mr-2" />
                      {t('loadDocument.import')}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoadDocument;